import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Lock, Network, Link2, Check, Info, X } from 'lucide-react';
import { Channel, CHANNEL_TYPE_BADGES } from '../types/channel';
import { useChannelShare } from '../hooks/useChannelShare';
import SubscribeButton from './SubscribeButton';
import ChannelSubscribersModal from './ChannelSubscribersModal';
import { useReadOnly } from '../hooks/useArchiveAccess';

interface ChannelHeaderProps {
  channel: Channel;
  isOwner?: boolean;
  myEmail?: string | null;
  onChannelUpdated?: (channel: Channel) => void;
}

const VisibilityIcon: React.FC<{ visibility: Channel['visibility'] }> = ({ visibility }) => {
  if (visibility === 'private') return <Lock size={13} aria-hidden />;
  if (visibility === 'network') return <Network size={13} aria-hidden />;
  return <Globe size={13} aria-hidden />;
};

const ChannelHeader: React.FC<ChannelHeaderProps> = ({ channel, isOwner, myEmail, onChannelUpdated }) => {
  const { t } = useTranslation();
  const { copying, showPrivateNotice, dismissPrivateNotice, copyShareLink } = useChannelShare(onChannelUpdated);
  // Updated from the Subscribe button so the count changes without reloading the channel.
  const [subscriberCount, setSubscriberCount] = useState(channel.subscriberCount ?? 0);
  useEffect(() => setSubscriberCount(channel.subscriberCount ?? 0), [channel.id, channel.subscriberCount]);
  const [showSubscribers, setShowSubscribers] = useState(false);
  const readOnly = useReadOnly(); // Archive Mode Grace period: sharing is off

  const initials = channel.displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const AVATAR_SIZE = 52;
  const hasBanner = !!channel.bannerUrl;

  return (
    <div
      style={{
        background: 'var(--card-bg, var(--theme-card, #fff))',
        borderBottom: '1px solid var(--theme-border, #e5e7eb)',
        transition: 'background 0.3s ease, border-color 0.3s ease',
      }}
    >
      {/* ── Banner zone — tall only when a real banner image is present ── */}
      <div
        style={{
          height: hasBanner ? 120 : 52,
          position: 'relative',
          overflow: 'hidden',
          background: hasBanner
            ? undefined
            : 'linear-gradient(135deg, var(--theme-navbar, var(--theme-primary, #1976d2)) 0%, var(--theme-primary, #1976d2) 100%)',
          transition: 'background 0.3s ease',
        }}
      >
        {hasBanner && (
          <img
            src={channel.bannerUrl!}
            alt=""
            aria-hidden
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
          />
        )}
      </div>

      {/* ── Avatar row: overlaps banner by half the avatar height ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        padding: '0 24px',
        marginTop: -(AVATAR_SIZE / 2),
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Avatar */}
        <div style={{
          width: AVATAR_SIZE, height: AVATAR_SIZE, borderRadius: '50%',
          border: '3px solid var(--card-bg, var(--theme-card, #fff))',
          overflow: 'hidden', flexShrink: 0,
          background: 'var(--theme-primary, #1976d2)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          {channel.avatarUrl ? (
            <img
              src={channel.avatarUrl}
              alt={channel.displayName}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 22, color: '#fff',
            }}>
              {initials}
            </div>
          )}
        </div>

        {/* Actions — right side, sits just below the banner line */}
        <div style={{ alignSelf: 'flex-start', marginTop: AVATAR_SIZE / 2 + 8, position: 'relative' }}>
          {!isOwner && (
            <SubscribeButton channel={channel} myEmail={myEmail} onSubscriberCountChange={setSubscriberCount} />
          )}
          {isOwner && !readOnly && (
            <>
              <button
                onClick={() => copyShareLink(channel)}
                aria-label={t('channel.copy_link_aria')}
                title={copying ? t('channel.copy_link_copied_title') : t('channel.copy_link_title')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 6, cursor: 'pointer',
                  fontSize: 13, fontWeight: 500,
                  background: copying ? 'var(--theme-primary, #1976d2)' : 'var(--surface-hover, #f3f4f6)',
                  color: copying ? '#fff' : 'var(--text-primary, #111)',
                  border: '1px solid var(--theme-border, #e5e7eb)',
                  transition: 'background 0.2s ease, color 0.2s ease',
                }}
              >
                {copying ? <Check size={14} aria-hidden /> : <Link2 size={14} aria-hidden />}
                {copying ? t('channel.copy_link_copied') : t('channel.copy_link')}
              </button>

              {/* Private channel notice — shown after copying */}
              {showPrivateNotice && (
                <div style={{
                  position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 50,
                  background: 'var(--card, #fff)',
                  border: '1px solid var(--border, #e5e7eb)',
                  borderLeft: '3px solid #f59e0b',
                  borderRadius: 8,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  padding: '10px 12px',
                  width: 280,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <Info size={15} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} aria-hidden />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: 'var(--foreground, #111)' }}>
                        {t('channel.private_notice_title', 'Canal privado')}
                      </p>
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--textSecondary, #6b7280)', lineHeight: 1.5 }}>
                        {t('channel.private_notice_body', 'Solo los miembros que añadas en Configuración podrán ver este canal y sus videos.')}
                      </p>
                    </div>
                    <button
                      onClick={dismissPrivateNotice}
                      aria-label={t('common.dismiss', 'Cerrar')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--textSecondary, #6b7280)', flexShrink: 0 }}
                    >
                      <X size={13} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Info block: always on card background, never over the banner ── */}
      <div style={{ padding: '8px 20px 12px' }}>
        {/* Name + visibility badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <h2 style={{
            margin: 0, fontSize: 18, fontWeight: 700,
            color: 'var(--card-title-color, var(--text-primary, #111))',
          }}>
            {CHANNEL_TYPE_BADGES[channel.type] && (
              <span style={{ marginRight: 4 }} aria-hidden>{CHANNEL_TYPE_BADGES[channel.type]}</span>
            )}
            {channel.displayName}
          </h2>

          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 500,
            padding: '2px 8px', borderRadius: 12,
            background: channel.visibility === 'private'
              ? 'var(--surface-hover, #f3f4f6)'
              : 'color-mix(in srgb, var(--theme-primary, #1976d2) 10%, transparent)',
            color: channel.visibility === 'private'
              ? 'var(--text-secondary, #6b7280)'
              : 'var(--theme-primary, #1976d2)',
            border: '1px solid',
            borderColor: channel.visibility === 'private'
              ? 'var(--theme-border, #e5e7eb)'
              : 'color-mix(in srgb, var(--theme-primary, #1976d2) 27%, transparent)',
          }}>
            <VisibilityIcon visibility={channel.visibility} />
            {t(`channel.visibility_${channel.visibility}`)}
          </span>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>@{channel.handle}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
            {t('channel.video_count', { count: channel.videoCount ?? 0 })}
          </span>
          {isOwner ? (
            <button
              type="button"
              onClick={() => setShowSubscribers(true)}
              title={t('channel.subscribers_view', 'See subscribers')}
              style={{
                fontSize: 12, padding: 0, background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--primary-text, #1976d2)', textDecoration: 'underline',
              }}
            >
              {subscriberCount} {t('channel.subscribers')}
            </button>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
              {subscriberCount} {t('channel.subscribers')}
            </span>
          )}
          {channel.subject && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>· {channel.subject}</span>
          )}
        </div>

        {/* Description */}
        {channel.description && (
          <p style={{
            margin: '4px 0 0', fontSize: 13, lineHeight: 1.5,
            color: 'var(--card-text-color, var(--text-secondary, #6b7280))',
          }}>
            {channel.description}
          </p>
        )}

        {/* Tags */}
        {channel.tags?.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {channel.tags.map((tag) => (
              <span key={tag} style={{
                fontSize: 11, padding: '2px 10px', borderRadius: 10,
                background: 'var(--surface-hover, #f3f4f6)',
                color: 'var(--text-secondary, #6b7280)',
                border: '1px solid var(--theme-border, #e5e7eb)',
              }}>
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {showSubscribers && (
        <ChannelSubscribersModal channel={channel} onClose={() => setShowSubscribers(false)} />
      )}
    </div>
  );
};

export default ChannelHeader;
